import React, { useState, useRef, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { downloadImage } from '../../gemini'; // Reusing download helper
import { CLOUDINARY_UPLOAD_URL, cloudinaryConfig } from '../../cloudinary';

// IndexedDB Helper for Large Video History
const DB_NAME = 'VideoGenStudioDB';
const STORE_NAME = 'video_history';

const initDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, 1);
        request.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'id' });
            }
        };
        request.onsuccess = (e) => resolve(e.target.result);
        request.onerror = (e) => reject(e.target.error);
    });
};

const saveHistoryToDB = async (history) => {
    try {
        const db = await initDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        await new Promise((resolve, reject) => {
            const clearReq = store.clear();
            clearReq.onsuccess = resolve;
            clearReq.onerror = reject;
        });
        for (const item of history) {
            store.add(item);
        }
    } catch (err) {
        console.error('IndexedDB save error:', err);
    }
};

const loadHistoryFromDB = async () => {
    try {
        const db = await initDB();
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        return new Promise((resolve) => {
            const request = store.getAll();
            request.onsuccess = () => {
                const data = request.result || [];
                resolve(data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
            };
            request.onerror = () => resolve([]);
        });
    } catch (err) {
        console.error('IndexedDB load error:', err);
        return [];
    }
};

const styles = {
    container: {
        padding: '32px',
        maxWidth: '1400px',
        margin: '0 auto',
        fontFamily: "'Inter', -apple-system, sans-serif",
    },
    header: {
        marginBottom: '40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
    },
    titleSection: {
        flex: 1,
    },
    title: {
        fontSize: '32px',
        fontWeight: '800',
        color: '#1a1d29',
        margin: 0,
        letterSpacing: '-0.5px',
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    },
    subtitle: {
        fontSize: '15px',
        color: '#64748b',
        marginTop: '8px',
    },
    // Tabs
    tabList: {
        display: 'flex',
        gap: '8px',
        marginBottom: '32px',
        background: '#f1f5f9',
        padding: '6px',
        borderRadius: '14px',
        width: 'fit-content',
    },
    tabItem: {
        padding: '10px 24px',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        border: 'none',
        outline: 'none',
    },
    activeTab: {
        background: '#ffffff',
        color: '#06C167',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.12)',
    },
    inactiveTab: {
        background: 'transparent',
        color: '#64748b',
    },
    // Main Grid
    layoutGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(400px, 450px) 1fr',
        gap: '32px',
        alignItems: 'start',
    },
    controlPanel: {
        background: '#ffffff',
        borderRadius: '24px',
        padding: '28px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 20px 25px -5px rgba(0,0,0,0.05)',
        border: '1px solid rgba(226, 232, 240, 0.8)',
    },
    previewPanel: {
        background: '#ffffff',
        borderRadius: '24px',
        padding: '28px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 20px 25px -5px rgba(0,0,0,0.05)',
        border: '1px solid rgba(226, 232, 240, 0.8)',
        minHeight: '600px',
        display: 'flex',
        flexDirection: 'column',
    },
    sectionTitle: {
        fontSize: '17px',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    formGroup: {
        marginBottom: '24px',
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '600',
        color: '#475569',
        marginBottom: '10px',
    },
    textArea: {
        width: '100%',
        minHeight: '120px',
        padding: '16px',
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        fontSize: '14px',
        color: '#1e293b',
        resize: 'none',
        transition: 'all 0.2s',
        outline: 'none',
        lineHeight: '1.6',
    },
    // Upload Area
    dropzone: {
        width: '100%',
        height: '180px',
        border: '2px dashed #cbd5e1',
        borderRadius: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s',
        background: '#f8fafc',
        overflow: 'hidden',
        position: 'relative',
    },
    dropzoneActive: {
        borderColor: '#06C167',
        background: '#f5f7ff',
    },
    uploadIcon: {
        fontSize: '32px',
        marginBottom: '12px',
        color: '#06C167',
    },
    uploadText: {
        fontSize: '13px',
        color: '#64748b',
        fontWeight: '500',
    },
    // Action Button
    generateBtn: {
        width: '100%',
        padding: '18px',
        background: 'linear-gradient(135deg, #06C167 0%, #4f46e5 100%)',
        color: '#ffffff',
        border: 'none',
        borderRadius: '16px',
        fontSize: '16px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.3s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        boxShadow: '0 10px 15px -3px rgba(99, 102, 241, 0.3)',
    },
    generateBtnLoading: {
        opacity: 0.8,
        cursor: 'progress',
    },
    // Video Preview
    videoContainer: {
        flex: 1,
        background: '#0f172a',
        borderRadius: '20px',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
    },
    videoBackdrop: {
        position: 'absolute',
        top: 0, left: 0, width: '100%', height: '100%',
        objectFit: 'cover',
        opacity: 0.3,
        filter: 'blur(20px)',
    },
    videoElement: {
        width: '100%',
        maxHeight: '100%',
        zIndex: 1,
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    },
    emptyPreview: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        color: '#94a3b8',
    },
    // Result Actions
    previewActions: {
        display: 'flex',
        gap: '12px',
        marginTop: '24px',
    },
    actionBtn: {
        flex: 1,
        padding: '14px',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        background: '#ffffff',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        color: '#475569',
        transition: 'all 0.2s',
    },
    // Settings Grid
    settingsGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
        marginTop: '20px',
    },
    select: {
        width: '100%',
        padding: '12px',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        fontSize: '13px',
        background: '#ffffff',
        outline: 'none',
    }
};

const DURATION_OPTIONS = [
    { label: '5 Saniye', value: 5 },
    { label: '10 Saniye', value: 10 },
    { label: '15 Saniye', value: 15 }
];

const RATIO_OPTIONS = [
    { label: '16:9 (Sinematik)', value: '16:9' },
    { label: '9:16 (Reels/Story)', value: '9:16' },
    { label: '1:1 (Kare)', value: '1:1' }
];

function VideoPreparation() {
    const { userData } = useAuth();
    const [activeTab, setActiveTab] = useState('text-to-video');
    const [prompt, setPrompt] = useState('');
    const [duration, setDuration] = useState(5);
    const [ratio, setRatio] = useState('16:9');
    const [sourceImage, setSourceImage] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedVideo, setGeneratedVideo] = useState(null);
    const [history, setHistory] = useState([]);
    const [status, setStatus] = useState('');

    const fileInputRef = useRef(null);

    useEffect(() => {
        loadHistoryFromDB().then(setHistory);
    }, []);

    useEffect(() => {
        if (history.length > 0) saveHistoryToDB(history);
    }, [history]);

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (evt) => setSourceImage(evt.target.result);
            reader.readAsDataURL(file);
        }
    };

    const handleGenerate = async () => {
        if (activeTab === 'text-to-video' && !prompt.trim()) {
            alert('Lütfen bir video tarifi girin.');
            return;
        }
        if (activeTab === 'image-to-video' && !sourceImage) {
            alert('Lütfen kaynak bir görsel yükleyin.');
            return;
        }

        setIsGenerating(true);
        setStatus('Hayal gücü canlandırılıyor...');

        try {
            // Simulated AI Video Generation stages
            setTimeout(() => setStatus('Sahneler kurgulanıyor...'), 2000);
            setTimeout(() => setStatus('Işık ve gölge efektleri ekleniyor...'), 4000);
            setTimeout(() => setStatus('Video render ediliyor...'), 6000);

            // Wait for simulator
            await new Promise(r => setTimeout(r, 7000));

            // Using a more stable sample video from a reliable source (Pexels / Sample-Videos)
            // This is a direct MP4 link that is usually more stable than Pixabay's dynamic vimeo links
            const sampleVideos = [
                'https://assets.mixkit.co/videos/preview/mixkit-stars-in-the-night-sky-11601-large.mp4',
                'https://assets.mixkit.co/videos/preview/mixkit-waves-in-the-ocean-1582-large.mp4',
                'https://assets.mixkit.co/videos/preview/mixkit-tree-with-yellow-flowers-1173-large.mp4'
            ];

            const randomVideo = sampleVideos[Math.floor(Math.random() * sampleVideos.length)];

            const resultVideo = {
                id: Date.now(),
                url: randomVideo,
                thumbnail: 'https://images.unsplash.com/photo-1492691523567-61723c27adff?q=80&w=400&auto=format&fit=crop',
                prompt: prompt || 'Görselden oluşturulan video',
                timestamp: new Date().toISOString(),
                duration,
                ratio
            };

            setGeneratedVideo(resultVideo);
            setHistory(prev => [resultVideo, ...prev].slice(0, 20));
        } catch (err) {
            console.error(err);
            alert('Video oluşturulurken bir hata oluştu.');
        } finally {
            setIsGenerating(false);
            setStatus('');
        }
    };

    const handleDownload = async () => {
        if (!generatedVideo) return;

        try {
            setStatus('Hazırlanıyor...');
            // Fetch the video and convert to blob for reliable download
            const response = await fetch(generatedVideo.url);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `muhasebe-ai-video-${Date.now()}.mp4`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setStatus('');
        } catch (err) {
            console.error('Download error:', err);
            // Fallback to simple link if fetch fails
            const link = document.createElement('a');
            link.href = generatedVideo.url;
            link.target = '_blank';
            link.download = `muhasebe-ai-video-${Date.now()}.mp4`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.titleSection}>
                    <h1 style={styles.title}>AI Video Stüdyosu</h1>
                    <p style={styles.subtitle}>Hayalinizdeki sahneleri tek bir cümleyle gerçeğe dönüştürün</p>
                </div>
            </div>

            <div style={styles.tabList}>
                <button
                    style={{ ...styles.tabItem, ...(activeTab === 'text-to-video' ? styles.activeTab : styles.inactiveTab) }}
                    onClick={() => setActiveTab('text-to-video')}
                >
                    ✍️ Metinden Video
                </button>
                <button
                    style={{ ...styles.tabItem, ...(activeTab === 'image-to-video' ? styles.activeTab : styles.inactiveTab) }}
                    onClick={() => setActiveTab('image-to-video')}
                >
                    🖼️ Görselden Video
                </button>
                <button
                    style={{ ...styles.tabItem, ...(activeTab === 'history' ? styles.activeTab : styles.inactiveTab) }}
                    onClick={() => setActiveTab('history')}
                >
                    🕒 Geçmiş
                </button>
            </div>

            {activeTab !== 'history' ? (
                <div style={styles.layoutGrid}>
                    <div style={styles.controlPanel}>
                        <h3 style={styles.sectionTitle}>
                            {activeTab === 'text-to-video' ? '📝 Video Tarifi' : '📤 Kaynak Görsel'}
                        </h3>

                        {activeTab === 'text-to-video' ? (
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Neye benzemesini istersiniz?</label>
                                <textarea
                                    style={styles.textArea}
                                    placeholder="Örn: Karlı dağların üzerinde uçan bir kartal, sinematik ışık, 4k..."
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                />
                            </div>
                        ) : (
                            <div style={styles.formGroup}>
                                <div
                                    style={{ ...styles.dropzone, ...(sourceImage ? { border: 'none' } : {}) }}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    {sourceImage ? (
                                        <img src={sourceImage} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Source" />
                                    ) : (
                                        <>
                                            <div style={styles.uploadIcon}>📸</div>
                                            <div style={styles.uploadText}>Bir görsel seçin veya sürükleyin</div>
                                        </>
                                    )}
                                    <input ref={fileInputRef} type="file" hidden accept="image/*" onChange={handleFileSelect} />
                                </div>
                                {sourceImage && (
                                    <button
                                        style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '13px', marginTop: '8px', cursor: 'pointer' }}
                                        onClick={() => setSourceImage(null)}
                                    >
                                        Görseli Kaldır
                                    </button>
                                )}
                            </div>
                        )}

                        <div style={styles.settingsGrid}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Süre</label>
                                <select
                                    style={styles.select}
                                    value={duration}
                                    onChange={(e) => setDuration(Number(e.target.value))}
                                >
                                    {DURATION_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                </select>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Boyut</label>
                                <select
                                    style={styles.select}
                                    value={ratio}
                                    onChange={(e) => setRatio(e.target.value)}
                                >
                                    {RATIO_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                </select>
                            </div>
                        </div>

                        <button
                            style={{ ...styles.generateBtn, ...(isGenerating ? styles.generateBtnLoading : {}) }}
                            onClick={handleGenerate}
                            disabled={isGenerating}
                        >
                            {isGenerating ? (
                                <>⏳ {status}</>
                            ) : (
                                <>🚀 Videoyu Oluştur</>
                            )}
                        </button>
                    </div>

                    <div style={styles.previewPanel}>
                        <h3 style={styles.sectionTitle}>🎬 Önizleme</h3>
                        <div style={styles.videoContainer}>
                            {isGenerating ? (
                                <div style={{ textAlign: 'center', zIndex: 10, color: 'white' }}>
                                    <div className="video-loader"></div>
                                    <p style={{ marginTop: '20px', fontWeight: '500' }}>{status}</p>
                                </div>
                            ) : generatedVideo ? (
                                <div style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <img src={generatedVideo.thumbnail} style={styles.videoBackdrop} alt="Backdrop" />
                                    <video
                                        key={generatedVideo.url}
                                        src={generatedVideo.url}
                                        style={styles.videoElement}
                                        controls
                                        autoPlay
                                        loop
                                        playsInline
                                        onError={(e) => {
                                            console.error("Video load error", e);
                                            // Fallback to a another stable link if this one fails
                                            if (!e.target.src.includes('w3schools')) {
                                                e.target.src = 'https://www.w3schools.com/html/mov_bbb.mp4';
                                            }
                                        }}
                                    />
                                    <div style={{ position: 'absolute', top: '20px', right: '20px', background: 'rgba(99, 102, 241, 0.9)', color: 'white', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', zIndex: 10 }}>
                                        AI OLUŞTURULDU
                                    </div>
                                </div>
                            ) : (
                                <div style={styles.emptyPreview}>
                                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>✨</div>
                                    <p>Henüz bir video oluşturulmadı</p>
                                    <p style={{ fontSize: '12px', marginTop: '8px' }}>Bir tarif girerek tasarıma başlayın</p>
                                </div>
                            )}
                        </div>

                        {generatedVideo && !isGenerating && (
                            <div style={styles.previewActions}>
                                <button style={{ ...styles.actionBtn, background: '#06C167', color: 'white', border: 'none' }} onClick={handleDownload}>
                                    📥 Videoyu İndir
                                </button>
                                <button style={styles.actionBtn} onClick={() => window.open(generatedVideo.url, '_blank')}>
                                    🔗 Kaynağı Aç
                                </button>
                                <button
                                    style={styles.actionBtn}
                                    onClick={() => {
                                        setGeneratedVideo(null);
                                        setPrompt('');
                                        setSourceImage(null);
                                    }}
                                >
                                    🔄 Yeni Oluştur
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '24px' }}>
                    {history.length === 0 ? (
                        <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px', background: 'white', borderRadius: '24px', color: '#94a3b8', border: '1px dashed #e2e8f0' }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗄️</div>
                            <p>Henüz geçmiş bulunmuyor.</p>
                        </div>
                    ) : history.map(item => (
                        <div key={item.id} style={{
                            background: 'white', borderRadius: '20px', overflow: 'hidden', border: '1px solid #e2e8f0',
                            transition: 'all 0.3s', cursor: 'pointer', position: 'relative'
                        }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                            onClick={() => {
                                setGeneratedVideo(item);
                                setActiveTab('text-to-video');
                            }}
                        >
                            <div style={{ height: '180px', position: 'relative' }}>
                                <img src={item.thumbnail} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Thumb" />
                                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'rgba(255,255,255,0.8)', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    ▶️
                                </div>
                            </div>
                            <div style={{ padding: '16px' }}>
                                <p style={{ fontSize: '13px', color: '#1e293b', fontWeight: '600', marginBottom: '8px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', height: '40px' }}>
                                    {item.prompt}
                                </p>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#64748b', borderTop: '1px solid #f1f5f9', paddingTop: '10px' }}>
                                    <span>{new Date(item.timestamp).toLocaleDateString('tr-TR')}</span>
                                    <span style={{ color: '#06C167', fontWeight: 'bold' }}>{item.duration}s | {item.ratio}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <style>{`
                .video-loader {
                    width: 48px;
                    height: 48px;
                    border: 5px solid #ffffff33;
                    border-bottom-color: #06C167;
                    border-radius: 50%;
                    display: inline-block;
                    box-sizing: border-box;
                    animation: rotation 1s linear infinite;
                }
                @keyframes rotation {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}

export default VideoPreparation;
